/**
 * Utility class that deals with dom-related actions.
 */
export class DOMElement {
  /**
   * Set or append a class to a dom element.
   *
   * @param   {str} className - The class name we want to add.
   * @param   {obj} domElement - The dom element to append the class to.
   */
  static addClass(className, domElement) {
    if (domElement && domElement.className.indexOf(className) === -1) {
      domElement.className += ` ${className}`;
      domElement.className = domElement.className.trim();
    }
  }

  /**
   * Get if a DOM element has a class.
   *
   * @param {str} className - The class name to look for.
   * @param {obj} domElement - The DOM element on which look for the class.
   */
  static hasClass(className, domElement) {
    return domElement && domElement.className.indexOf(className) !== -1;
  }

  /**
   * Remove a class from a dom element.
   *
   * @param   {str} className - The class name we want to add.
   * @param   {obj} domElement - The dom element to append the class to.
   */
  static removeClass(className, domElement) {
    const reg = new RegExp(`(\\s|^)${className}(\\s|$)`);

    if (domElement && domElement.className.indexOf(className) !== -1) {
      domElement.className = domElement.className.replace(reg, ' ').trim();
    }
  }

  /**
   * Add multiple classes to a DOM element.
   *
   * @param {Array} classes - Array of classes to add to the element.
   * @param {obj} domElement - The DOM element to which add the classes.
   */
  static addClasses(classes, domElement) {
    classes.forEach((className) => {
      DOMElement.addClass(className, domElement);
    });
  }

  /**
   * Remove multiple classes from a DOM element.
   *
   * @param {Array} classes - Array of classes to remove from the element.
   * @param {obj} domElement - The DOM element on which remove the classes.
   */
  static removeClasses(classes, domElement) {
    classes.forEach((className) => {
      DOMElement.removeClass(className, domElement);
    });
  }

  /**
   * Add a child DOM element to another DOM element.
   *
   * @param {obj} child - Child DOM element to add.
   * @param {obj} domElement - The DOM element to which add the child.
   */
  static appendChild(child, domElement) {
    domElement.appendChild(child);
  }

  /**
   * Add multiple child DOM elements to another DOM element.
   *
   * @param {Array} children - Array of DOM elements to add.
   * @param {obj} domElement - The DOM element to which append the children elements.
   */
  static appendChildren(children, domElement) {
    children.forEach((child) => {
      DOMElement.appendChild(child, domElement);
    });
  }

  /**
   * Set new width and height to a DOM element.
   * Width and height are in the CSS form.
   * E.g. '100px'.
   *
   * @param {string} width - The new width of the DOM element.
   * @param {string} height - The new height of the DOM element.
   * @param {obj} domElement - The DOM element to which apply the new size.
   */
  static setSize(width, height, domElement) {
    DOMElement.setWidth(width, domElement);
    DOMElement.setHeight(height, domElement);
  }

  /**
   * Set a new width to a DOM element.
   *
   * @param {string} width - The new width of the DOM element.
   * @param {obj} domElement - The DOM element to which set the new width.
   */
  static setWidth(width, domElement) {
    domElement.style.width = width;
  }

  /**
   * Set a new height to a DOM element.
   *
   * @param {string} height - The new height of the DOM element.
   * @param {obj} domElement - The DOM element to which set the new height.
   */
  static setHeight(height, domElement) {
    domElement.style.height = height;
  }

  /**
   * Set a new left and top to a DOM element.
   *
   * @param {string} left - The new left position of the DOM element.
   * @param {string} top - The new top position of the DOM element.
   * @param {obj} domElement - The DOM element to which set the new position.
   */
  static setPosition(left, top, domElement) {
    DOMElement.setLeft(left, domElement);
    DOMElement.setTop(top, domElement);
  }

  /**
   * Set a new left position to a DOM element.
   *
   * @param {string} left - The new left position to set to the DOM element.
   * @param {obj} domElement - The DOM element to which set the new left.
   */
  static setLeft(left, domElement) {
    domElement.style.left = left;
  }

  /**
   * Set a new top position to a DOM element.
   *
   * @param {string} top - The new top position to set to the DOM element.
   * @param {obj} domElement - The DOM element to which set the new top position.
   */
  static setTop(top, domElement) {
    domElement.style.top = top;
  }

  /**
   * Set the position type of the DOM element.
   * E.g. 'absolute' or 'relative'.
   *
   * @param {string} pos - The new position type to set to the DOM element.
   * @param {obj} domElement - The DOM element to which set the new position type.
   */
  static setPositionType(pos, domElement) {
    domElement.style.position = pos;
  }

  /**
   * Get the page X offset.
   *
   * @returns {number} The page X offset.
   */
  static getPageYOffset() {
    return 'pageYOffset' in window ? window.pageYOffset : document.documentElement.scrollTop;
  }

  /**
   * Get the page Y offset.
   *
   * @returns {number} The page Y offset.
   */
  static getPageXOffset() {
    return 'pageXOffset' in window ? window.pageXOffset : document.documentElement.scrollLeft;
  }
}
